/**
 * 앱 셸 — 사이드바(C-2 IA) · 상단바 · 우측 드로어.
 *
 * 화면마다 헤더를 따로 그리던 것을 여기 하나로 모은다. 이유는 미관이 아니라 **위치의 항상성**
 * 이다: 사용자는 "작업함이 어디 있나"를 화면마다 다시 찾으면 안 된다. 대화·작업함·파일함·관리는
 * 같은 자리에서 같은 모양으로 열린다.
 *
 * 사이드바에는 작업 메뉴 · 프로젝트 트리(F-6) · 최근 대화가 산다. 시안(WORKS/UI)에 있던 검색은
 * 아직 없다 — 백엔드에 검색 API 가 없기 때문이다. 없는 데이터를 자리채움으로 그리지 않는다(P1).
 *
 * 프로젝트 트리의 규칙: **프로젝트에 속한 대화는 그 아래에만** 나오고 '최근 대화'에서는 빠진다.
 * 같은 대화가 두 곳에 보이면 사용자는 둘이 다른 것이라고 읽는다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useSession } from '@/auth/SessionProvider'
import { useChatStore } from '@/chat/store'
import { createProject, fetchProjects } from '@/chat/projects'
import type { Project } from '@/chat/projects'
import { fetchThreads, threadLabel } from '@/chat/threads'
import type { ThreadListResponse, ThreadSummary } from '@/chat/threads'
import { LocaleSwitcher } from '@/i18n/LocaleSwitcher'
import { useTheme } from '@/ui/theme'
import {
  Archive,
  ChevronDown,
  ChevronRight,
  Folder,
  ListChecks,
  LogOut,
  MessageSquare,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  ShieldCheck,
  SquarePen,
  Sun,
  Workflow,
  X,
} from '@/ui/icons'

/** CSS 의 ≤900px 오버레이 경계와 **같은 수**여야 한다 — 어긋나면 사이드바가 화면을 덮은 채
 *  닫기 버튼만 사라지는 구간이 생긴다. */
const DESKTOP = 901

const isMac =
  typeof navigator !== 'undefined' && /Mac|iP/.test(navigator.platform || navigator.userAgent)

export interface AppShellProps {
  /** 상단바 제목. */
  title: string
  /** 제목 옆 상태 pill — 단일 JobStatus 에서 파생된 것만 온다(표면별 개별 상태 금지). */
  pill?: ReactNode
  /** 우측 드로어 본문. 주면 상단바에 '작업 상세' 버튼이 생긴다. */
  drawer?: ReactNode
  drawerTitle?: string
  children: ReactNode
}

export function AppShell({ title, pill, drawer, drawerTitle, children }: AppShellProps) {
  const { t, i18n } = useTranslation()
  const { session, logout } = useSession()
  const { theme, toggle } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const resetChat = useChatStore((state) => state.reset)
  const openThread = useChatStore((state) => state.openThread)
  const currentThread = useChatStore((state) => state.threadId)
  // 목록이 갱신되는 시점 = 턴이 끝나 transcript 가 저장된 뒤. 그 신호로 status 를 쓴다.
  const runStatus = useChatStore((state) => state.status)

  const threads = useQuery<ThreadListResponse>({
    queryKey: ['chat-threads'],
    queryFn: () => fetchThreads(),
    enabled: session.authed,
    staleTime: 10_000,
  })
  const { refetch: refetchThreads } = threads
  useEffect(() => {
    // 새 대화의 첫 턴이 끝나면 목록에 없던 항목이 생긴다 — 그때 한 번 다시 묻는다.
    if (runStatus === 'idle' || runStatus === 'done') void refetchThreads()
  }, [runStatus, refetchThreads])

  const queryClient = useQueryClient()
  const projects = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    enabled: session.authed,
    staleTime: 10_000,
  })
  const projectList = useMemo(() => projects.data ?? [], [projects.data])

  const allThreads = useMemo(() => threads.data?.threads ?? [], [threads.data])
  // 프로젝트에 속한 대화는 트리 아래에만 그린다 — '최근 대화'에도 나오면 같은 대화가 둘로 보인다.
  const threadsByProject = useMemo(() => {
    const grouped = new Map<string, ThreadSummary[]>()
    for (const thread of allThreads) {
      if (!thread.project_id) continue
      const bucket = grouped.get(thread.project_id)
      if (bucket) bucket.push(thread)
      else grouped.set(thread.project_id, [thread])
    }
    return grouped
  }, [allThreads])
  const threadList = useMemo(() => allThreads.filter((t) => !t.project_id), [allThreads])

  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= DESKTOP)
  // 펼친 프로젝트 — 화면 로컬 상태(서버에 저장하지 않는다). 사용자가 지금 무엇을 보고 있는지는
  // 기기·탭마다 다르고, 그걸 서버에 남기면 다른 탭에서 트리가 저 혼자 접혔다 펴진다.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [drawerOpen, setDrawerOpen] = useState(false)

  const closeOnMobile = () => {
    if (window.innerWidth < DESKTOP) setSidebarOpen(false)
  }

  const newChat = useCallback(() => {
    resetChat()
    navigate('/')
    if (window.innerWidth < DESKTOP) setSidebarOpen(false)
  }, [navigate, resetChat])

  const startInProject = useChatStore((state) => state.startInProject)
  const newProject = useMutation({
    mutationFn: (name: string) => createProject(name),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      // 만들자마자 그 프로젝트로 들어간다 — 목록에 줄 하나가 늘고 끝나면 "그래서 뭘 하라고?"가
      // 된다. 방금 만든 것을 바로 쓸 수 있는 자리에 놓는다.
      setExpanded((prev) => new Set(prev).add(created.project_id))
      navigate(`/projects/${created.project_id}`)
      closeOnMobile()
    },
  })

  // 새 프로젝트 이름은 **사이드바 안에서** 받는다(window.prompt 아님). 브라우저 모달은 페이지를
  // 통째로 막고, 이름을 짓는 동안 정작 참고할 기존 프로젝트 목록을 가린다.
  const [naming, setNaming] = useState(false)
  const [draftName, setDraftName] = useState('')

  const submitNewProject = () => {
    const name = draftName.trim()
    if (!name) return
    newProject.mutate(name)
    setDraftName('')
    setNaming(false)
  }

  const toggleProject = (projectId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }

  const openChatThread = (thread: ThreadSummary) => {
    void openThread(thread.thread_id, thread.project_id ?? null)
    navigate('/')
    closeOnMobile()
  }

  // Ctrl/⌘+K — 새 대화. 라벨도 플랫폼에 맞춘다(맥에서 "Ctrl K"라고 적으면 아무도 안 누른다).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        newChat()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [newChat])

  /*
   * dev 개방(auth_required=false)에서는 authed·isAdmin 이 true 인데 user 가 없다.
   * 그 상태를 빈 이름 + "관리자"로 그리면 두 가지가 틀린다 — 줄이 비어 고장처럼 보이고,
   * 아무도 로그인하지 않았는데 관리자가 있는 것처럼 말한다. 상태를 있는 그대로 적는다.
   */
  const user = session.user
  const who = user?.login_id ?? ''
  const displayName = who || t('nav.dev_open')
  const roleLabel = user
    ? session.isAdmin
      ? t('nav.role_admin')
      : t('nav.role_user')
    : t('nav.dev_open_note')

  return (
    <div className="app">
      {/* ============ 사이드바 ============ */}
      <aside
        className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}
        aria-label={t('nav.sidebar')}
        /* 닫힌 사이드바는 탭 순서에서 빠진다 — 화면 밖 버튼으로 포커스가 가면
           사용자는 "포커스가 사라졌다"고 느낀다(패리티 16). */
        inert={!sidebarOpen}
      >
        <div className="sb-head">
          {/* 배경 이미지 워드마크 — 텍스트 노드가 없으므로 스크린리더에는 role/label 로 알린다. */}
          <span className="wordmark" role="img" aria-label="L.LINE" />
          <button
            type="button"
            className="icon-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label={t('nav.sidebar_close')}
            title={t('nav.sidebar_close')}
          >
            <PanelLeftClose size={17} />
          </button>
        </div>

        <button type="button" className="new-chat" onClick={newChat}>
          <SquarePen size={15} />
          <span>{t('nav.new_chat')}</span>
          <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>

        <nav className="sb-scroll" aria-label={t('nav.sidebar')}>
          <div className="sb-label">{t('nav.section')}</div>
          <NavLink
            to="/"
            end
            className={({ isActive }) => `sb-item ${isActive ? 'active' : ''}`}
            onClick={closeOnMobile}
          >
            <MessageSquare size={15} />
            <span className="label">{t('nav.chat_short')}</span>
          </NavLink>
          <NavLink
            to="/jobs"
            className={({ isActive }) => `sb-item ${isActive ? 'active' : ''}`}
            onClick={closeOnMobile}
          >
            <ListChecks size={15} />
            <span className="label">{t('nav.jobs')}</span>
          </NavLink>
          <NavLink
            to="/files"
            className={({ isActive }) => `sb-item ${isActive ? 'active' : ''}`}
            onClick={closeOnMobile}
          >
            <Archive size={15} />
            <span className="label">{t('nav.files')}</span>
          </NavLink>
          {session.isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `sb-item ${isActive ? 'active' : ''}`}
              onClick={closeOnMobile}
            >
              <Settings size={15} />
              <span className="label">{t('nav.admin')}</span>
            </NavLink>
          )}

          {/* ── 프로젝트(F-6) — 대화를 묶는 사용자 작업 공간 ──
              트리를 펼치면 그 프로젝트의 대화가 나온다. 대화 수는 서버가 세어 준다(빈 프로젝트도
              나온다 — 방금 만든 것이 목록에서 사라지면 고장으로 읽힌다). */}
          <div className="sb-label sb-label-row">
            <span>{t('nav.projects')}</span>
            <button
              type="button"
              className="icon-btn sb-add"
              onClick={() => setNaming(true)}
              disabled={newProject.isPending}
              aria-label={t('projects.new')}
              title={t('projects.new')}
            >
              <Plus size={14} />
            </button>
          </div>
          {naming && (
            <input
              className="input sb-naming"
              autoFocus
              value={draftName}
              maxLength={120}
              placeholder={t('projects.new_prompt')}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitNewProject()
                // Esc 로 취소 — 이름 짓다 말고 빠져나올 길이 없으면 사이드바에 빈 칸이 남는다.
                if (event.key === 'Escape') {
                  setDraftName('')
                  setNaming(false)
                }
              }}
              onBlur={submitNewProject}
            />
          )}
          {projectList.map((project) => {
            const open = expanded.has(project.project_id)
            const inProject = threadsByProject.get(project.project_id) ?? []
            const activeProject = location.pathname === `/projects/${project.project_id}`
            return (
              <div key={project.project_id}>
                <div className={`sb-item sb-project ${activeProject ? 'active' : ''}`}>
                  {/* 펼치기와 열기를 **다른 버튼**으로 나눈다 — 하나로 묶으면 대화를 보려고
                      누를 때마다 화면이 옮겨 가고, 화면을 열려고 누르면 트리가 접힌다. */}
                  <button
                    type="button"
                    className="sb-twisty"
                    onClick={() => toggleProject(project.project_id)}
                    aria-expanded={open}
                    aria-label={open ? t('projects.collapse') : t('projects.expand')}
                  >
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <button
                    type="button"
                    className="sb-project-open"
                    aria-current={activeProject ? 'page' : undefined}
                    onClick={() => {
                      navigate(`/projects/${project.project_id}`)
                      closeOnMobile()
                    }}
                  >
                    <Folder size={15} />
                    <span className="label">{project.name}</span>
                    {project.thread_count > 0 && (
                      <span className="sb-count">{project.thread_count}</span>
                    )}
                  </button>
                </div>
                {open && (
                  <>
                    {inProject.map((thread) => {
                      const active =
                        thread.thread_id === currentThread && location.pathname === '/'
                      return (
                        <button
                          key={thread.thread_id}
                          type="button"
                          className={`sb-item sb-child ${active ? 'active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                          onClick={() => openChatThread(thread)}
                        >
                          <span className="label">
                            {threadLabel(thread, i18n.language, (at) =>
                              t('nav.untitled', { at }),
                            )}
                          </span>
                        </button>
                      )
                    })}
                    <button
                      type="button"
                      className="sb-item sb-child sb-child-new"
                      onClick={() => {
                        startInProject(project.project_id)
                        navigate('/')
                        closeOnMobile()
                      }}
                    >
                      <SquarePen size={13} />
                      <span className="label">{t('projects.new_chat_here')}</span>
                    </button>
                  </>
                )}
              </div>
            )
          })}
          {projectList.length === 0 && !projects.isLoading && (
            <div className="sb-note">{t('projects.empty')}</div>
          )}

          {/* 최근 대화 — 목록은 백엔드가 요청자 소유로 좁혀 준다(프런트는 거르지 않는다).
              **프로젝트에 속한 대화는 여기 없다** — 트리 아래에만 있다(같은 대화가 두 곳에
              보이면 사용자는 둘이 다른 것이라고 읽는다).
              제목이 없는 대화는 지어내지 않고 갱신 시각으로 표시한다. */}
          <div className="sb-label">{t('nav.recent')}</div>
          {threadList.map((thread) => {
            const active = thread.thread_id === currentThread && location.pathname === '/'
            return (
              <button
                key={thread.thread_id}
                type="button"
                className={`sb-item ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={() => openChatThread(thread)}
              >
                <span className="label">
                  {threadLabel(thread, i18n.language, (at) => t('nav.untitled', { at }))}
                </span>
              </button>
            )
          })}
          {threadList.length === 0 && (
            <div className="sb-note">
              {threads.isLoading
                ? t('common.loading')
                : (threads.data?.detail ?? t('nav.recent_empty'))}
            </div>
          )}
        </nav>

        <div className="sb-foot">
          <div className="avatar" aria-hidden="true">
            {who.slice(0, 1).toUpperCase() || '·'}
          </div>
          <div className="who">
            <div className="name">{displayName}</div>
            <div className="role">{roleLabel}</div>
          </div>
          <LocaleSwitcher />
          <button
            type="button"
            className="icon-btn"
            onClick={toggle}
            aria-label={t('nav.theme')}
            title={t('nav.theme')}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => void logout()}
            aria-label={t('auth.logout')}
            title={t('auth.logout')}
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      {sidebarOpen && (
        <button
          type="button"
          className="backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label={t('nav.sidebar_close')}
          tabIndex={-1}
        />
      )}

      {/* ============ 메인 ============ */}
      <main className="main">
        <header className="topbar">
          {!sidebarOpen && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('nav.sidebar_open')}
              title={t('nav.sidebar_open')}
            >
              <PanelLeftOpen size={18} />
            </button>
          )}
          <div className="tb-title">
            <span className="thread-title">{title}</span>
            {pill}
          </div>
          <div className="tb-actions">
            <span className="secure-badge">
              <ShieldCheck size={13} /> {t('nav.internal')}
            </span>
            {drawer && (
              <button
                type="button"
                className={`drawer-btn ${drawerOpen ? 'on' : ''}`}
                onClick={() => setDrawerOpen((open) => !open)}
                aria-expanded={drawerOpen}
              >
                <Workflow size={15} />
                <span>{drawerTitle ?? t('drawer.title')}</span>
              </button>
            )}
          </div>
        </header>

        {children}
      </main>

      {/* ============ 우측 드로어 ============ */}
      {drawer && (
        <aside
          className={`drawer ${drawerOpen ? 'open' : ''}`}
          aria-label={drawerTitle ?? t('drawer.title')}
          inert={!drawerOpen}
        >
          <div className="drawer-head">
            <div>
              <span>{t('drawer.subtitle')}</span>
              <strong>{drawerTitle ?? t('drawer.title')}</strong>
            </div>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setDrawerOpen(false)}
              aria-label={t('drawer.close')}
            >
              <X size={17} />
            </button>
          </div>
          <div className="drawer-body">{drawer}</div>
        </aside>
      )}
    </div>
  )
}
